"use client"

import { useMemo } from "react"
import { NestedRowTable } from "../../DataTable/NestedRowTable"
import type { ColumnDefinition, FndnWeightVolume, SchemaField } from "../../../types/project"
import { getAllFoundationQuantityProperties } from "../../../constants/foundationQuantityTabProperties"
import { getDefaultUnit, getMeasureType } from "../../../utils/unitUtils"
import { debugLog } from "../../../constants/debug"
import type { TabComponentProps } from "../../../types/tabs"

export function FoundationQuantityTab({ selectedNode, projectData, menuAction }: TabComponentProps) {
  // Generate column definitions from schema
  const columns = useMemo(() => {
    const foundationSchema = projectData?.schemas?.Foundation_Volume_Weight_Schema
    if (!foundationSchema) {
      debugLog("No Foundation_Volume_Weight_Schema found in projectData")
      return []
    }

    debugLog("Generating columns with schema:", foundationSchema)

    const allProperties = getAllFoundationQuantityProperties(foundationSchema)
    // Map the fields in the order defined in FOUNDATION_QUANTITY_PROPERTIES
    return allProperties
      .map((fieldKey) => {
        // Find the schema entry that matches our property key
        const schemaEntries = Object.entries(foundationSchema) as [string, SchemaField][]
        const matchingSchema = schemaEntries.find(([key]) => key === fieldKey)

        if (!matchingSchema) {
          debugLog(`No schema found for field: ${fieldKey}`)
          return null
        }

        const [_, field] = matchingSchema

        const defaultUnit = getDefaultUnit(field)

        const column: ColumnDefinition = {
          key: fieldKey,
          field: field.field || fieldKey,
          title: field.title || fieldKey,
          units: defaultUnit,
          measure: getMeasureType(defaultUnit),
          type: field.type,
          sortable: true,
          filterable: true,
          align: field.type === "number" ? ("right" as const) : ("left" as const),
        }

        debugLog("Created column definition:", column)

        return column
      })
      .filter((col): col is ColumnDefinition => col !== null)
  }, [projectData])

  // Process foundation data with new structure
  const foundationData = useMemo(() => {
    const nodeName = selectedNode?.name || ""
    const nodeType = selectedNode?.type || ""

    debugLog("Processing foundation data for:", {
      nodeName,
      nodeType,
      hasProjectData: !!projectData,
    })

    if (!projectData?.foundationVolumeQuantity?.Sections) {
      debugLog("No foundation quantities data available")
      return []
    }

    // Find matching section and its foundation info
    const findMatchingData = () => {
      // If looking for Line data, return the first section's FndnWeightVolume that has Type: 'line'
      if (nodeType === "line") {
        const lineData = projectData.foundationVolumeQuantity.Sections.find(
          (section) => section.FndnWeightVolume.Type.toLowerCase() === "line",
        )
        return lineData?.FndnWeightVolume
      }

      // For sections, find the matching section
      if (nodeType === "section") {
        const sectionData = projectData.foundationVolumeQuantity.Sections.find(
          (section) => section.FndnWeightVolume.Name === nodeName,
        )
        return sectionData?.FndnWeightVolume
      }

      // For towers, find the tower in the appropriate section
      if (nodeType === "tower") {
        const parentSection = selectedNode?.parent?.name
        const sectionData = projectData.foundationVolumeQuantity.Sections.find(
          (section) => section.FndnWeightVolume.Name === parentSection,
        )
        if (sectionData?.FndnWeightVolume.FndnWeightVolume) {
          return sectionData.FndnWeightVolume.FndnWeightVolume.find((tower) => tower.Name === nodeName)
        }
      }

      return undefined
    }

    const matchingData = findMatchingData()
    debugLog("Found matching data:", matchingData)

    if (!matchingData) {
      return []
    }

    // Process the foundation info into the table structure
    const processFndnWeightVolume = (foundation: FndnWeightVolume, parentId = ""): FndnWeightVolume => {
      const currentId = parentId ? `${parentId}-${foundation.Name}` : foundation.Name
      return {
        ...foundation,
        id: currentId,
        FndnWeightVolume: foundation.FndnWeightVolume?.map((child) => processFndnWeightVolume(child, currentId)),
      }
    }

    const processedData = processFndnWeightVolume(matchingData)
    debugLog("Processed data:", processedData)

    return [processedData]
  }, [selectedNode, projectData])

  if (!projectData) {
    return <div>No project data available</div>
  }

  debugLog("Rendering table with:", {
    hasData: foundationData.length > 0,
    dataLength: foundationData.length,
    columnsLength: columns.length,
  })

  return (
    <div style={{ padding: "16px" }}>
      <NestedRowTable
        data={foundationData}
        columns={columns}
        levelKey="id"
        childrenKey="FndnWeightVolume"
        tableName="foundation-quantity"
        title={`${selectedNode?.name || "Foundation"} Quantities`}
        menuAction={menuAction}
      />
    </div>
  )
}

